import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useTRPC } from "@/integrations/trpc/react";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/buyers/forgot-password")({
	component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
	const trpc = useTRPC();
	const [email, setEmail] = useState("");
	const [submitted, setSubmitted] = useState(false);

	const forgotPasswordMutation = useMutation(
		trpc.auth.forgotPassword.mutationOptions({
			onSuccess: () => {
				setSubmitted(true);
				toast.success("Password reset email sent!");
			},
			onError: () => {
				// Still show success to prevent email enumeration
				setSubmitted(true);
				toast.success("Password reset email sent!");
			},
		}),
	);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		forgotPasswordMutation.mutate({ email });
	};

	if (submitted) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
				<Card className="w-full max-w-md">
					<CardHeader className="space-y-1">
						<CardTitle className="text-2xl font-bold text-center">
							Check your email
						</CardTitle>
						<CardDescription className="text-center">
							We've sent a password reset link to {email}
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<p className="text-sm text-muted-foreground text-center">
							Didn't receive the email? Check your spam folder or try again.
						</p>
						<div className="flex flex-col gap-2">
							<Button variant="outline" onClick={() => setSubmitted(false)}>
								Try another email
							</Button>
							<Button variant="link" asChild>
								<a href="/buyers/login">
									<ArrowLeft className="mr-2 h-4 w-4" />
									Back to login
								</a>
							</Button>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
			<Card className="w-full max-w-md">
				<CardHeader className="space-y-1">
					<CardTitle className="text-2xl font-bold text-center">
						Forgot password?
					</CardTitle>
					<CardDescription className="text-center">
						Enter your email and we'll send you a reset link
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit} className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="email">Email</Label>
							<Input
								id="email"
								type="email"
								placeholder="john.doe@example.gov"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
							/>
						</div>

						<Button type="submit" className="w-full" disabled={forgotPasswordMutation.isPending}>
							{forgotPasswordMutation.isPending ? "Sending..." : "Send reset link"}
						</Button>
					</form>

					<div className="mt-4 text-center">
						<a
							href="/buyers/login"
							className="text-sm text-primary underline underline-offset-4 hover:text-primary/80 inline-flex items-center"
						>
							<ArrowLeft className="mr-1 h-3 w-3" />
							Back to login
						</a>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
